<template>
  <b-modal v-model="localVisible" title="Presentation when this page opens" @ok="onSave" @hide="onHide" size="xl">
    <template #title>
      Presentation when this page opens
      <modal-help topic="page-open-rules" />
    </template>
    <p class="por-hint">
      Presentation. These rules show a banner or redirect when this page opens. Showing or hiding a view is set on that view.
    </p>
    <p class="por-hint">
      Every matching rule runs. A condition reads either the logged-in user's row or the record that opened this page. Those are separate choices.
    </p>

    <div v-if="localRules.length" class="por-rule-list">
      <div v-for="(rule, idx) in localRules" :key="rule.id" class="por-rule-item" :class="{ 'por-rule-editing': editingIndex === idx }">
        <button type="button" class="por-rule-open" @click="editRule(idx)">
          <span class="por-rule-body">{{ ruleSummary(rule) }}</span>
        </button>
        <button type="button" class="por-remove" @click.stop="removeRule(idx)" aria-label="Remove rule">&times;</button>
      </div>
    </div>

    <div v-if="!localRules.length && !isEditing" class="por-empty">No presentation rules. The page opens as designed.</div>

    <div v-if="isEditing" class="por-edit">
      <div class="por-edit-title">{{ editingIndex !== null ? 'Edit' : 'New' }} rule</div>

      <div class="por-section-head">
        <span>Conditions</span>
        <div v-if="editConditions.length > 1" class="por-match">
          <button type="button" :class="{ active: editMatchMode === 'all' }" @click="editMatchMode = 'all'">ALL</button>
          <button type="button" :class="{ active: editMatchMode === 'any' }" @click="editMatchMode = 'any'">ANY</button>
        </div>
      </div>
      <p v-if="!editConditions.length" class="por-hint">No conditions. This rule runs every time the page opens.</p>

      <div v-for="(cond, ci) in editConditions" :key="ci" class="por-condition">
        <div class="por-sources" role="group" aria-label="Where this condition reads">
          <label class="por-source">
            <input type="radio" value="user" v-model="cond.subject" @change="onSubjectChange(cond)" />
            Logged-in user's row
          </label>
          <label class="por-source">
            <input type="radio" value="record" v-model="cond.subject" @change="onSubjectChange(cond)" />
            Record that opened this page
          </label>
        </div>

        <select v-if="cond.subject === 'user'" v-model="cond.field" class="por-select" aria-label="Field on the logged-in user" @change="onFieldChange(cond, userColumns)">
          <option value="" disabled>Field on the logged-in user...</option>
          <option v-for="col in userColumns" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title || col.uniqueTitle }}</option>
        </select>
        <select v-else v-model="cond.field" class="por-select" aria-label="Field on the record that opened this page" @change="onFieldChange(cond, recordColumns)">
          <option value="" disabled>Field on the record that opened this page...</option>
          <option v-for="col in recordColumns" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title || col.uniqueTitle }}</option>
        </select>

        <p v-if="cond.subject === 'user' && !userColumns.length" class="por-hint">Add a login form that uses a spreadsheet before a logged-in user condition can read a field.</p>
        <p v-if="cond.subject === 'record' && !recordColumns.length" class="por-hint">This page is not opened from a record, so a record condition has no row to read.</p>

        <select v-model="cond.operator" class="por-select">
          <option v-for="op in operatorsFor(cond.dataType)" :key="op.key" :value="op.key">{{ op.label }}</option>
        </select>
        <input v-if="!isBlank(cond.operator)" v-model="cond.value" class="por-input" type="text" />
        <button type="button" class="por-remove" @click="editConditions.splice(ci, 1)" aria-label="Remove condition">&times;</button>
      </div>
      <button type="button" class="por-add" @click="editConditions.push(newCondition())">+ condition</button>

      <div class="por-section-head"><span>Actions</span></div>
      <div v-for="(action, ai) in editActions" :key="ai" class="por-condition">
        <select v-model="action.type" class="por-select" @change="onActionType(action)">
          <option value="banner">Show a dismissible banner</option>
          <option value="redirect">Redirect</option>
        </select>
        <input v-if="action.type === 'banner'" v-model="action.message" class="por-input" type="text" placeholder="Message" />
        <template v-if="action.type === 'redirect'">
          <select v-model="action.destination" class="por-select">
            <option value="page">Another page in this app</option>
            <option value="external">An external address</option>
          </select>
          <select v-if="action.destination !== 'external'" v-model="action.pageUniqueTitle" class="por-select" aria-label="Page to open">
            <option value="" disabled>Page...</option>
            <option v-for="item in pageChoices" :key="item.uniqueTitle" :value="item.uniqueTitle">{{ item.title }} ({{ item.uniqueTitle }})</option>
          </select>
          <input v-else v-model="action.url" class="por-input" type="text" placeholder="https://help.example.com/article" />
          <p v-if="action.destination === 'external'" class="por-hint">
            This address is sent only when its host is listed in App settings, on Embed. Any other host stays on this page. A redirect to another page in this app does not use that list.
          </p>
        </template>
        <button type="button" class="por-remove" @click="editActions.splice(ai, 1)" aria-label="Remove action">&times;</button>
      </div>
      <button type="button" class="por-add" @click="editActions.push(newAction())">+ action</button>

      <div class="por-edit-actions">
        <button type="button" class="por-save" @click="saveRule">Save rule</button>
        <button type="button" class="por-cancel" @click="cancelEdit">Cancel</button>
      </div>
    </div>

    <button v-if="!isEditing" type="button" class="por-add por-add-rule" @click="startAddRule">+ add rule</button>
  </b-modal>
</template>

<script>
import { operatorsForDataType, isBlankOperator } from '../../../../shared/display-rules.js'
import { findTopLevelLoginSpreadSheet } from '../../../../shared/logged-in-user-default.js'
import { findPageRecordSpreadSheet } from '../../../../shared/page-record-link.js'

const SKIP_TYPES = new Set(['password', 'image', 'file'])
const TYPE_MAP = {
  text: 'string',
  number: 'number',
  dateTime: 'datetime',
  formula: 'string',
  richText: 'string',
  relationship: 'relationship',
  checkbox: 'checkbox',
  multiselect: 'string',
  address: 'string',
  duration: 'number',
  sequence: 'number',
  signature: 'signature'
}

function generateRuleId () {

  return `por_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

function columnDataType (column) {

  return TYPE_MAP[column && column.type] || 'string'
}

export default {
  name: 'PageOpenRuleEditor',
  props: {
    visible: { type: Boolean, default: false },
    page: { type: Object, default: null },
    pages: { type: Array, default: () => [] },
    spreadsheets: { type: Array, default: () => [] }
  },
  emits: ['update:visible', 'save'],
  data () {

    return {
      localVisible: false,
      localRules: [],
      isEditing: false,
      editingIndex: null,
      editConditions: [],
      editMatchMode: 'all',
      editActions: []
    }
  },
  computed: {
    userColumns () {

      return this.columnsOn(findTopLevelLoginSpreadSheet(this.pages))
    },
    recordColumns () {

      return this.columnsOn(findPageRecordSpreadSheet(this.pages, this.page))
    },
    pageChoices () {

      const current = this.page && this.page.uniqueTitle

      return (this.pages || []).filter(item => item && item.uniqueTitle && item.uniqueTitle !== current).map(item => ({
        uniqueTitle: item.uniqueTitle,
        title: item.title || item.uniqueTitle
      }))
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler (val) {

        this.localVisible = val
        if (val) this.loadFromPage()
      }
    },
    localVisible (val) {

      this.$emit('update:visible', val)
    }
  },
  methods: {
    columnsOn (uniqueTitle) {

      const sheet = (this.spreadsheets || []).find(item => item && item.uniqueTitle === uniqueTitle)

      return ((sheet && sheet.columns) || []).filter(column => column && column.uniqueTitle && !SKIP_TYPES.has(column.type))
    },
    loadFromPage () {

      const rules = this.page && this.page.pageOpenRules
      const list = Array.isArray(rules) ? rules : (rules && rules.rules) || []

      this.localRules = JSON.parse(JSON.stringify(list)).map(rule => ({
        ...rule,
        actions: (rule.actions || []).filter(action => action && action.type !== 'hideView')
      })).filter(rule => rule.actions.length)
      this.cancelEdit()
    },
    operatorsFor (dataType) {

      return operatorsForDataType(dataType || 'string')
    },
    isBlank (operator) {

      return isBlankOperator(operator)
    },
    newCondition () {

      return {
        subject: this.recordColumns.length ? 'record' : 'user',
        field: '',
        operator: '=',
        value: '',
        dataType: 'string'
      }
    },
    newAction () {

      return { type: 'banner', message: '', destination: 'page', viewUniqueTitle: '', pageUniqueTitle: '', url: '' }
    },
    onSubjectChange (cond) {

      cond.field = ''
      cond.value = ''
      cond.dataType = 'string'
      cond.operator = '='
    },
    onFieldChange (cond, columns) {

      const column = (columns || []).find(item => item.uniqueTitle === cond.field)

      cond.dataType = columnDataType(column)
      const operators = this.operatorsFor(cond.dataType)

      if (!operators.some(op => op.key === cond.operator)) cond.operator = operators[0] ? operators[0].key : '='
    },
    onActionType (action) {

      action.message = ''
      action.viewUniqueTitle = ''
      action.pageUniqueTitle = ''
      action.url = ''
      if (action.type === 'redirect' && !action.destination) action.destination = 'page'
    },
    startAddRule () {

      this.isEditing = true
      this.editingIndex = null
      this.editConditions = []
      this.editMatchMode = 'all'
      this.editActions = [this.newAction()]
    },
    editRule (idx) {

      const rule = this.localRules[idx]

      this.isEditing = true
      this.editingIndex = idx
      this.editConditions = JSON.parse(JSON.stringify(rule.conditions || []))
      this.editMatchMode = rule.conditionMatchMode || 'all'
      this.editActions = JSON.parse(JSON.stringify(rule.actions && rule.actions.length ? rule.actions : [this.newAction()]))
    },
    cancelEdit () {

      this.isEditing = false
      this.editingIndex = null
      this.editConditions = []
      this.editActions = []
    },
    cleanAction (action) {

      if (!action) return null

      if (action.type === 'banner' && String(action.message || '').trim()) {

        return { type: 'banner', message: String(action.message).trim() }
      }

      if (action.type === 'redirect' && action.destination === 'external' && String(action.url || '').trim()) {

        return { type: 'redirect', destination: 'external', url: String(action.url).trim() }
      }

      if (action.type === 'redirect' && action.pageUniqueTitle) {

        return { type: 'redirect', destination: 'page', pageUniqueTitle: action.pageUniqueTitle }
      }

      return null
    },
    saveRule () {

      const actions = this.editActions.map(action => this.cleanAction(action)).filter(Boolean)

      if (!actions.length) return

      const conditions = this.editConditions.filter(cond => cond && cond.field && (cond.subject === 'user' || cond.subject === 'record')).map(cond => ({
        subject: cond.subject,
        field: cond.field,
        operator: cond.operator,
        value: this.isBlank(cond.operator) ? '' : cond.value,
        dataType: cond.dataType || 'string'
      }))
      const rule = {
        id: this.editingIndex !== null && this.localRules[this.editingIndex] ? this.localRules[this.editingIndex].id : generateRuleId(),
        conditionMatchMode: this.editMatchMode === 'any' ? 'any' : 'all',
        conditions,
        actions
      }

      if (this.editingIndex !== null) this.localRules.splice(this.editingIndex, 1, rule)
      else this.localRules.push(rule)

      this.cancelEdit()
    },
    removeRule (idx) {

      this.localRules.splice(idx, 1)
      if (this.editingIndex === idx) this.cancelEdit()
      else if (this.editingIndex !== null && this.editingIndex > idx) this.editingIndex -= 1
    },
    ruleSummary (rule) {

      const conditions = (rule.conditions || []).map(cond => {
        const who = cond.subject === 'user' ? 'Logged-in user' : 'Record that opened this page'
        return `${who} ${cond.field} ${cond.operator} ${cond.value || ''}`.trim()
      })
      const when = conditions.length ? conditions.join(rule.conditionMatchMode === 'any' ? ' or ' : ' and ') : 'Always'
      const actions = (rule.actions || []).map(action => {
        if (action.type === 'banner') return 'banner'
        if (action.type === 'redirect' && action.destination === 'external') return 'external redirect'
        if (action.type === 'redirect') return `redirect to ${action.pageUniqueTitle}`
        return action.type
      })

      return `${when} → ${actions.join(', ') || 'no action'}`
    },
    onSave () {

      if (this.isEditing) this.saveRule()

      this.$emit('save', { rules: this.localRules })
    },
    onHide () {

      this.cancelEdit()
    }
  }
}
</script>

<style scoped>
.por-hint {
  color: #8a8a88;
  font-size: 0.8rem;
  margin-bottom: 8px;
}
.por-select, .por-input {
  width: 100%;
  background: #1a1a17;
  color: #e8e8e6;
  border: 1px solid #3a3a37;
  border-radius: 4px;
  padding: 6px 8px;
  margin-bottom: 8px;
}
.por-rule-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}
.por-rule-item {
  display: flex;
  align-items: center;
  border: 1px solid #3a3a37;
  border-radius: 6px;
}
.por-rule-editing {
  border-color: #9ad12b;
}
.por-rule-open {
  flex: 1;
  text-align: left;
  background: transparent;
  border: 0;
  color: #e8e8e6;
  padding: 8px 10px;
  cursor: pointer;
}
.por-rule-body {
  font-size: 0.78rem;
}
.por-empty {
  color: #8a8a88;
  margin-bottom: 12px;
}
.por-edit {
  border: 1px solid #3a3a37;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
}
.por-edit-title {
  font-weight: 600;
  margin-bottom: 10px;
  color: #e8e8e6;
}
.por-section-head, .por-edit-actions, .por-sources {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.por-source {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #3a3a37;
  border-radius: 4px;
  padding: 6px 8px;
  color: #e8e8e6;
}
.por-condition {
  border-top: 1px solid #2a2a27;
  padding-top: 8px;
  margin-bottom: 8px;
}
.por-match button, .por-add, .por-save, .por-cancel {
  border: 1px solid #3a3a37;
  background: transparent;
  color: #e8e8e6;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
}
.por-match button.active, .por-save {
  border-color: #9ad12b;
  color: #9ad12b;
}
.por-add {
  margin-bottom: 8px;
}
.por-remove {
  border: 0;
  background: transparent;
  color: #8a8a88;
  cursor: pointer;
}
</style>
